/**
 * A bar displayed under the Rovo Chat header when the active conversation
 * belongs to a Rovo Space. Shows the space emoji and name.
 *
 * - To exit the space, use the back-arrow button in the chat header.
 */
import React, { memo, useMemo } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { ResourcedEmoji } from '@atlaskit/emoji/element';
import { type EmojiProvider } from '@atlaskit/emoji/resource';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { createEmojiProvider } from '../../common/utils/EmojiProviderUtils';

/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values -- fixed pixel sizes and raw font-feature-settings */
const styles = cssMap({
	spaceContent: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.075'),
		minWidth: '0px',
		flexGrow: 1,
	},
	emojiWrapper: {
		flexShrink: 0,
	},
	spaceName: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: token('color.text.subtle'),
		fontFeatureSettings: "'liga' off, 'calt' off",
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		fontStyle: 'normal',
	},
	container: {
		paddingTop: token('space.075'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.150'),
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border.disabled'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border.disabled'),
	},
	clickableContainer: {
		cursor: 'pointer',
		backgroundColor: token('elevation.surface'),
		'&:hover': {
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
});
/* eslint-enable @atlaskit/ui-styling-standard/no-unsafe-values */

export interface SpaceBarProps {
	/** The emoji shortname or unicode character for the space. */
	emoji?: string | null;
	/** The display name of the space. */
	name: string;
	/** The cloud ID used to resolve site-specific emojis via the emoji provider. */
	cloudId: string;
	/** Optional emoji provider — if supplied, takes precedence over the internally created one. */
	emojiProvider?: Promise<EmojiProvider>;
	/** Optional callback fired when the user opens the selected space. */
	onClick?: () => void;
	/** Optional test ID for automated testing. */
	testId?: string;
}

const SpaceBarComponent = ({
	emoji,
	name,
	cloudId,
	emojiProvider: emojiProviderProp,
	onClick,
	testId = 'space-bar',
}: SpaceBarProps): React.JSX.Element => {
	const internalEmojiProvider = useMemo(() => createEmojiProvider(cloudId), [cloudId]);
	const emojiProvider = emojiProviderProp ?? internalEmojiProvider;

	const content = (
		<Box xcss={styles.spaceContent}>
			<Box xcss={styles.emojiWrapper}>
				{emoji ? (
					<ResourcedEmoji
						emojiId={{ id: emoji, shortName: emoji }}
						emojiProvider={emojiProvider}
						fitToHeight={16}
					/>
				) : (
					<FolderClosedIcon
						label=""
						color={token('color.text.subtlest')}
						testId={`${testId}-folder-icon`}
					/>
				)}
			</Box>
			<Box xcss={styles.spaceName}>{name}</Box>
		</Box>
	);

	if (onClick) {
		return (
			<Pressable
				xcss={cx(styles.container, styles.clickableContainer)}
				testId={testId}
				onClick={onClick}
				aria-label={name}
			>
				{content}
			</Pressable>
		);
	}

	return (
		<Box xcss={styles.container} testId={testId}>
			{content}
		</Box>
	);
};

export const SpaceBar: React.MemoExoticComponent<
	({ emoji, name, cloudId, emojiProvider, onClick, testId }: SpaceBarProps) => React.JSX.Element
> = memo(SpaceBarComponent);
